import { useCallback, useMemo, useState } from "react";
import { PageHeader } from "@/components/data-display/page-header";
import { LoadingTable } from "@/components/ui/system-state";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/feedback";
import { Table, TableWrapper } from "@/components/ui/table";
import { StatusDot } from "@/components/ui/status";
import { WipHint } from "@/components/ui/wip-hint";
import { toast } from "@/components/ui/toast";
import { useInvoiceExceptions, useInvoiceResolutions } from "@/hooks/use-invoices";
import {
  exceptionStatePresentation,
  exceptionTypePresentation,
  resolutionKindPresentation,
  resolutionStatePresentation,
} from "@/features/invoicing/status";
import { formatMoney } from "@/features/invoicing/money";
import type { Exception, Money, Refund, Resolution } from "@/features/invoicing/types";
import { RESOLUTIONS_COPY as COPY } from "@/features/invoicing/resolutions/copy";
import {
  approveResolution,
  cancelResolution,
  refusalFromOutcome,
  rejectResolution,
  type DecisionRefusal,
} from "@/features/invoicing/resolutions/decisions";
import { DecisionRefusalNotice } from "@/features/invoicing/resolutions/decision-refusal";
import { ResolutionPacket } from "@/features/invoicing/resolutions/resolution-packet";
import { ReadRefusalNotice } from "@/features/invoicing/resolutions/read-refusal";
import { useInvoicePayments } from "@/features/invoicing/resolutions/use-invoice-payments";

/**
 * `/stablecoins/invoices/resolutions` — four-eyes approval of a proposed invoice
 * resolution or refund (page spec `invoice-resolution-approval`, ALL-5908).
 *
 * Reads: the queue of exceptions whose state is `RESOLUTION_PROPOSED`
 * (`useInvoiceExceptions`), the resolutions recorded against the selected
 * exception (`useInvoiceResolutions` — the only read path the invoicing client
 * exposes for resolutions, see that hook's module header), and the invoice's
 * payments (`useInvoicePayments`) that make up the packet's constituent
 * payments.
 *
 * Writes: `POST .../approve`, `POST .../reject` and `POST .../cancel` through
 * the helpers in `./decisions`. Approve and reject go through the house
 * step-up helper exactly as the transfer approval page does; a refused decision
 * is shown with the service's reason code and support reference and is never
 * retried, bypassed or re-submitted with a different key.
 *
 * This page cannot propose. `invoice:resolve.propose` and
 * `invoice:refund.propose` are the spec's `expected_denies` for every action
 * here, and an approver who could raise their own proposal would defeat the
 * separation the page exists to enforce.
 */
export default function InvoiceResolutionsPage() {
  const queue = useInvoiceExceptions({ state: "RESOLUTION_PROPOSED" });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Default to the first proposal so the packet is reachable without a click;
  // an explicit choice wins once the operator makes one.
  const selected: Exception | null = useMemo(() => {
    const explicit = queue.data.find((row) => row.exceptionId === selectedId);
    return explicit ?? queue.data[0] ?? null;
  }, [queue.data, selectedId]);

  const resolutions = useInvoiceResolutions(selected?.exceptionId);
  const payments = useInvoicePayments(selected?.invoiceId);

  const [pending, setPending] = useState<"approve" | "reject" | "cancel" | null>(null);
  const [refusal, setRefusal] = useState<DecisionRefusal | null>(null);
  const [refund, setRefund] = useState<Refund | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectReasonError, setRejectReasonError] = useState<string | null>(null);

  // The proposal the approver is deciding. A `PROPOSED` resolution is the one
  // awaiting a decision; anything else is the record of a decision already made.
  const resolution: Resolution | null = useMemo(() => {
    const list = resolutions.data;
    if (!list || list.length === 0) return null;
    return list.find((row) => row.state === "PROPOSED") ?? list[0];
  }, [resolutions.data]);

  const selectRow = useCallback((exceptionId: string) => {
    setSelectedId(exceptionId);
    setRefusal(null);
    setRefund(null);
    setRejectReason("");
    setRejectReasonError(null);
  }, []);

  /**
   * A decision succeeded. The packet's own reads are re-issued so the audit trail
   * shows the approver; the queue is deliberately left as it was, so the proposal
   * the approver just acted on does not vanish mid-confirmation. "Refresh queue"
   * re-reads it.
   */
  const afterDecision = useCallback(
    (message: string, decisionRefund: Refund | null) => {
      toast({ variant: "success", title: message });
      setRefund(decisionRefund);
      resolutions.refresh();
      payments.refresh();
    },
    [resolutions, payments],
  );

  const onApprove = useCallback(async () => {
    if (!resolution) return;
    setPending("approve");
    setRefusal(null);
    const outcome = await approveResolution(resolution);
    setPending(null);
    if (outcome.result.ok) {
      afterDecision(COPY.approvedToast, outcome.result.decision.refund);
      return;
    }
    setRefusal(refusalFromOutcome(outcome));
  }, [resolution, afterDecision]);

  const onReject = useCallback(async () => {
    if (!resolution) return;
    // The contract requires a reason on a rejection. An empty one is a field the
    // approver must fill in, so it is checked here rather than sent and refused.
    if (rejectReason.trim() === "") {
      setRejectReasonError(COPY.rejectReasonRequired);
      return;
    }
    setRejectReasonError(null);
    setPending("reject");
    setRefusal(null);
    const outcome = await rejectResolution(resolution, rejectReason.trim());
    setPending(null);
    if (outcome.result.ok) {
      afterDecision(COPY.rejectedToast, outcome.result.decision.refund);
      return;
    }
    setRefusal(refusalFromOutcome(outcome));
  }, [resolution, rejectReason, afterDecision]);

  const onCancel = useCallback(async () => {
    if (!resolution) return;
    setPending("cancel");
    setRefusal(null);
    const outcome = await cancelResolution(resolution);
    setPending(null);
    if (outcome.ok) {
      afterDecision(COPY.withdrawnToast, null);
      return;
    }
    // Cancel is not a four-eyes decision and is not wrapped in step-up, so its
    // refusal never carries a step-up outcome.
    setRefusal({
      reasonCode: outcome.reasonCode,
      supportReference: outcome.supportReference,
      capability: outcome.capability,
      stepUpFailed: false,
    });
  }, [resolution, afterDecision]);

  const refreshAll = useCallback(() => {
    queue.refresh();
    resolutions.refresh();
    payments.refresh();
  }, [queue, resolutions, payments]);

  const header = <PageHeader title={COPY.title} subtitle={COPY.subtitle} actions={<WipHint />} />;

  if (queue.loading && queue.data.length === 0) {
    return (
      <div>
        {header}
        <LoadingTable rows={4} cols={5} label={COPY.loadingLabel} />
      </div>
    );
  }

  if (queue.error && queue.data.length === 0) {
    return (
      <div>
        {header}
        <ReadRefusalNotice error={queue.error} onRetry={queue.refresh} subject="the proposals" />
      </div>
    );
  }

  return (
    <div>
      {header}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
        <Card>
          <CardBody className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle>{COPY.queueHeading}</CardTitle>
              <Button variant="secondary" size="sm" onClick={refreshAll} disabled={queue.loading}>
                Refresh
              </Button>
            </div>

            {queue.error && queue.data.length > 0 && (
              <p className="text-xs text-danger">{`Could not refresh the queue (${queue.error.reasonCode}).`}</p>
            )}

            {queue.data.length === 0 ? (
              <EmptyState title={COPY.queueEmptyTitle} description={COPY.queueEmptyBody} />
            ) : (
              <TableWrapper>
                <Table className="dense">
                  <thead>
                    <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                      <th className="px-3 py-2.5">{COPY.exceptionColumn}</th>
                      <th className="px-3 py-2.5">{COPY.kindColumn}</th>
                      <th className="px-3 py-2.5">{COPY.amountColumn}</th>
                      <th className="px-3 py-2.5">{COPY.stateColumn}</th>
                      <th className="px-3 py-2.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {queue.data.map((row) => {
                      const exceptionType = exceptionTypePresentation(row.type);
                      const exceptionState = exceptionStatePresentation(row.state);
                      const isSelected = selected?.exceptionId === row.exceptionId;
                      return (
                        <tr
                          key={row.exceptionId}
                          className="border-b border-line last:border-0"
                          aria-selected={isSelected}
                          data-testid={`resolution-queue-row-${row.exceptionId}`}
                        >
                          <td className="px-3 py-2.5">
                            <span className="flex flex-col">
                              <span className="font-mono text-sm text-ink">{row.exceptionId}</span>
                              <span className="text-xs text-muted">{exceptionType.label}</span>
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            {resolutions.data && isSelected ? (
                              <ResolutionKindCell resolutions={resolutions.data} />
                            ) : (
                              <span className="text-sm text-muted">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <QueueAmount value={row.amount} />
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="inline-flex items-center gap-1.5 text-sm text-ink">
                              <StatusDot tone={exceptionState.tone} />
                              {exceptionState.label}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => selectRow(row.exceptionId)}
                            >
                              {COPY.openLabel}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrapper>
            )}

            {queue.hasMore && (
              <Button
                variant="secondary"
                size="sm"
                onClick={queue.loadMore}
                disabled={queue.loadingMore}
              >
                {queue.loadingMore ? "Loading…" : "Load more"}
              </Button>
            )}
            {queue.loadMoreError && (
              <p className="text-xs text-danger">{`More proposals could not be read (${queue.loadMoreError.reasonCode}).`}</p>
            )}
          </CardBody>
        </Card>

        <div className="flex flex-col gap-4">
          {refusal && <DecisionRefusalNotice refusal={refusal} onReload={refreshAll} />}

          {resolutions.loading && !resolutions.data && (
            <LoadingTable rows={3} cols={2} label="Loading the evidence packet" />
          )}

          {resolutions.error && !resolutions.data && (
            <ReadRefusalNotice
              error={resolutions.error}
              onRetry={resolutions.refresh}
              subject="this proposal"
            />
          )}

          {selected && resolution && (
            <ResolutionPacket
              exception={selected}
              resolution={resolution}
              payments={{ data: payments.data, loading: payments.loading, error: payments.error }}
              onReloadPayments={payments.refresh}
              refund={refund}
              pending={pending}
              rejectReason={rejectReason}
              onRejectReasonChange={(value) => {
                setRejectReason(value);
                if (value.trim() !== "") setRejectReasonError(null);
              }}
              rejectReasonError={rejectReasonError}
              onApprove={() => void onApprove()}
              onReject={() => void onReject()}
              onCancel={() => void onCancel()}
            />
          )}

          {selected && !resolution && !resolutions.loading && !resolutions.error && (
            <EmptyState
              title="No proposal is recorded for this exception"
              description="The exception is marked as having a proposed resolution, but the service returned none for it."
            />
          )}

          {!selected && (
            <Card>
              <CardBody>
                <p className="text-sm text-muted">{COPY.selectPrompt}</p>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

/** The disposition of the selected exception's proposal, in the queue row. */
function ResolutionKindCell({ resolutions }: { resolutions: Resolution[] }) {
  const proposed = resolutions.find((row) => row.state === "PROPOSED") ?? resolutions[0];
  if (!proposed) return <span className="text-sm text-muted">—</span>;
  const kind = resolutionKindPresentation(proposed.kind);
  const state = resolutionStatePresentation(proposed.state);
  return (
    <span className="flex flex-col">
      <Badge variant="info" dot={false}>
        {kind.label}
      </Badge>
      <span className="mt-1 text-xs text-muted">{state.label}</span>
    </span>
  );
}

/** An exception amount: base units, then the formatted form when the helper can read it. */
function QueueAmount({ value }: { value: Money }) {
  let display: string | null = null;
  try {
    display = `${formatMoney(value)} ${value.asset?.symbol ?? ""}`.trim();
  } catch {
    display = null;
  }
  return (
    <span className="flex flex-col">
      <span className="font-mono text-sm text-ink">{value.amount}</span>
      {display && <span className="text-xs text-muted">{display}</span>}
    </span>
  );
}
